import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PhoneIcon from '@mui/icons-material/Phone';
import PersonIcon from '@mui/icons-material/Person';
import { callAPI } from 'api/ApiUtils';
import { getListAPI as getMlmuserList } from 'components/mlmuser/MlmuserAPI';

const initialDummyMsgs = [
  'Template queued for review',
  'Sent to 24 recipients',
  'Campaign draft saved',
];

export default function CampainAggr() {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [error, setError] = useState('');
  const [titleFilter, setTitleFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [wabaStatusFilter, setWabaStatusFilter] = useState('APPROVED');
  const [seniorPartnerFilter, setSeniorPartnerFilter] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const [mlmuserRows, setMlmuserRows] = useState([]);
  const [mlmuserLoading, setMlmuserLoading] = useState(false);
  const [mlmNameFilter, setMlmNameFilter] = useState('');
  const [mlmPhoneFilter, setMlmPhoneFilter] = useState('');
  const [mlmMemberFilter, setMlmMemberFilter] = useState('all');

  useEffect(() => {
    setLoading(true);
    callAPI(
      'https://api.platform.appmachine.in/getwabatemplatelist',
      'POST',
      {},
      (response) => {
        setRows(response?.wabatemplateInfoList || []);
        setError('');
        setLoading(false);
      },
      (message) => {
        setError(message || 'Unable to load campaign templates.');
        setLoading(false);
      }
    );
  }, []);

  useEffect(() => {
    setMlmuserLoading(true);
    getMlmuserList(
      (list) => {
        setMlmuserRows(list || []);
        setMlmuserLoading(false);
      },
      () => {
        setMlmuserLoading(false);
      }
    );
  }, []);

  const categoryOptions = useMemo(() => ['all', ...new Set(rows.map((item) => item.category).filter(Boolean))], [rows]);
  const wabaStatusOptions = useMemo(() => ['all', ...new Set(rows.map((item) => item.wabastatus).filter(Boolean))], [rows]);

  const filteredRows = useMemo(() => {
    return rows.filter((item) => {
      const matchesTitle = titleFilter.trim()
        ? (item.title || '').toLowerCase().includes(titleFilter.trim().toLowerCase())
        : true;
      const matchesCategory = categoryFilter === 'all' ? true : item.category === categoryFilter;
      const matchesWabaStatus = wabaStatusFilter === 'all' ? true : item.wabastatus === wabaStatusFilter;
      return matchesTitle && matchesCategory && matchesWabaStatus;
    });
  }, [rows, titleFilter, categoryFilter, wabaStatusFilter]);

  const filteredMlmusers = useMemo(() => {
    return (mlmuserRows || []).filter((user) => {
      const matchesName = mlmNameFilter.trim()
        ? (user.title || user.firstname || '').toLowerCase().includes(mlmNameFilter.trim().toLowerCase())
        : true;
      const matchesPhone = mlmPhoneFilter.trim()
        ? (user.phone || user.mobilenumber || '').toString().includes(mlmPhoneFilter.trim())
        : true;

      let matchesMember = true;
      if (mlmMemberFilter === 'new') {
        matchesMember = user.partnershipstatus !== 'CONFIRMED';
      } else if (mlmMemberFilter === 'associates') {
        matchesMember = user.partnershipstatus === 'CONFIRMED';
      } else if (mlmMemberFilter === 'notOnboarded') {
        matchesMember = Number(user.userid || 0) === 0;
      }

      const matchesSeniorPartner = seniorPartnerFilter.trim()
        ? getSeniorPartnerId(user.seniorpartnerid).toLowerCase().includes(seniorPartnerFilter.trim().toLowerCase())
        : true;

      return matchesName && matchesPhone && matchesMember && matchesSeniorPartner;
    });
  }, [mlmuserRows, mlmNameFilter, mlmPhoneFilter, mlmMemberFilter, seniorPartnerFilter]);

  const getSeniorPartnerId = (srpartnerid) => {
    const user = mlmuserRows.find((u) => u.userid === srpartnerid);
    return user ? `${user.title || user.firstname || 'N/A'}` : srpartnerid || 'N/A';
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      return new Date(dateString).toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      });
    } catch (error) {
      return '-';
    }
  };

  const openTemplateDialog = (template) => {
    setSelectedTemplate(template);
    setDialogOpen(true);
  };

  const closeTemplateDialog = () => {
    setDialogOpen(false);
    setSelectedTemplate(null);
  };

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>
          Campaign Mgmt
        </Typography>
      </Box>

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}

      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <TextField
          size="small"
          label="Title"
          value={titleFilter}
          onChange={(event) => setTitleFilter(event.target.value)}
          sx={{ minWidth: 220 }}
        />
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Category</InputLabel>
          <Select
            label="Category"
            value={categoryFilter}
            onChange={(event) => setCategoryFilter(event.target.value)}
          >
            {categoryOptions.map((option) => (
              <MenuItem key={option} value={option}>
                {option === 'all' ? 'All' : option}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>WABA Status</InputLabel>
          <Select
            label="WABA Status"
            value={wabaStatusFilter}
            onChange={(event) => setWabaStatusFilter(event.target.value)}
          >
            {wabaStatusOptions.map((option) => (
              <MenuItem key={option} value={option}>
                {option === 'all' ? 'All' : option}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <Paper sx={{ overflow: 'hidden' }}>
        <TableContainer sx={{ maxHeight: '72vh' }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, backgroundColor: 'primary.light', position: 'sticky', top: 0, zIndex: 1 }}>
                  Title
                </TableCell>
                <TableCell sx={{ fontWeight: 600, backgroundColor: 'primary.light', position: 'sticky', top: 0, zIndex: 1 }}>
                  Category
                </TableCell>
                <TableCell sx={{ fontWeight: 600, backgroundColor: 'primary.light', position: 'sticky', top: 0, zIndex: 1 }}>
                  Waba Status
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={3} align="center" sx={{ py: 6 }}>
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={3} align="center" sx={{ py: 6 }}>
                    No templates found.
                  </TableCell>
                </TableRow>
              ) : filteredRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={3} align="center" sx={{ py: 6 }}>
                    No templates match the selected filters.
                  </TableCell>
                </TableRow>
              ) : (
                filteredRows.map((item) => (
                  <TableRow key={item.id} hover sx={{ cursor: 'pointer' }} onClick={() => openTemplateDialog(item)}>
                    <TableCell>{item.title || '-'}</TableCell>
                    <TableCell>{item.category || '-'}</TableCell>
                    <TableCell>{item.wabastatus || '-'}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog open={dialogOpen} onClose={closeTemplateDialog} fullWidth maxWidth="xl">
        <DialogTitle sx={{ pr: 6 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              {selectedTemplate?.title || 'Campaign Details'}
            </Typography>
            <IconButton onClick={closeTemplateDialog} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ bgcolor: 'grey.50' }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1.4fr' }, gap: 2 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Paper sx={{ p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Message Template Text
                </Typography>
                <Paper
                  variant="outlined"
                  sx={{
                    mt: 1,
                    p: 2,
                    whiteSpace: 'pre-wrap',
                    bgcolor: 'white',
                    minHeight: 180,
                  }}
                >
                  {selectedTemplate?.msgtemplatetext || '-'}
                </Paper>
              </Paper>

              <Paper sx={{ p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                  Sent Msg List
                </Typography>
                {initialDummyMsgs.map((msg) => (
                  <Box
                    key={msg}
                    sx={{
                      p: 1.25,
                      mb: 1,
                      borderRadius: 1,
                      bgcolor: 'grey.100',
                      fontSize: 14,
                    }}
                  >
                    {msg}
                  </Box>
                ))}
              </Paper>
            </Box>

            <Paper sx={{ p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    User(s)
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'primary.main',
                      backgroundColor: 'primary.100',
                      px: 1.5,
                      py: 0.5,
                      borderRadius: 2,
                      fontWeight: 600,
                    }}
                  >
                    ({mlmuserRows.length})
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ mb: 3 }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    mb: 2,
                    flexWrap: 'wrap',
                  }}
                >
                  <Box
                    sx={{
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      p: 1,
                      position: 'relative',
                      backgroundColor: 'white',
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        position: 'absolute',
                        top: -8,
                        left: 8,
                        backgroundColor: 'white',
                        px: 1,
                        fontSize: '12px',
                        color: 'text.secondary',
                      }}
                    >
                      Association
                    </Typography>
                    <RadioGroup
                      row
                      value={mlmMemberFilter}
                      onChange={(event) => setMlmMemberFilter(event.target.value)}
                      sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
                    >
                      <FormControlLabel value="new" control={<Radio size="small" />} label="Pending" />
                      <FormControlLabel value="associates" control={<Radio size="small" />} label="Confirmed" />
                      <FormControlLabel value="all" control={<Radio size="small" />} label="All" />
                      <FormControlLabel value="notOnboarded" control={<Radio size="small" />} label="Not Onboarded" />
                    </RadioGroup>
                  </Box>

                  <TextField
                    size="small"
                    placeholder="Search by name..."
                    value={mlmNameFilter}
                    onChange={(event) => setMlmNameFilter(event.target.value)}
                    sx={{ maxWidth: 170, flexShrink: 0 }}
                  />
                    <TextField
                      size="small"
                      placeholder="Search by mobile..."
                      value={mlmPhoneFilter}
                      onChange={(event) => setMlmPhoneFilter(event.target.value.replace(/\D/g, ''))}
                      sx={{ maxWidth: 150, flexShrink: 0 }}
                      inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', maxLength: 15 }}
                    />
                  <TextField
                    size="small"
                    placeholder="Search by senior partner..."
                    value={seniorPartnerFilter}
                    onChange={(event) => setSeniorPartnerFilter(event.target.value)}
                    sx={{ maxWidth: 220, flexShrink: 0 }}
                  />

                  {(mlmNameFilter || mlmPhoneFilter || seniorPartnerFilter || mlmMemberFilter === 'new' || mlmMemberFilter === 'associates' || mlmMemberFilter === 'notOnboarded') && (
                    <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}>
                      {filteredMlmusers.length}/{mlmuserRows.length}
                    </Typography>
                  )}
                </Box>
              </Box>

              <TableContainer
                component={Paper}
                sx={{
                  boxShadow: 2,
                  maxHeight: '58vh',
                  overflowY: 'auto',
                  overflowX: 'auto',
                }}
              >
                <Table stickyHeader sx={{ minWidth: 1000 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600, backgroundColor: 'primary.light', position: 'sticky', top: 0, zIndex: 1, minWidth: 180 }}>
                        User Info
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, backgroundColor: 'primary.light', position: 'sticky', top: 0, zIndex: 1, minWidth: 120 }}>
                        Contact
                      </TableCell>
                    <TableCell sx={{ fontWeight: 600, backgroundColor: 'primary.light', position: 'sticky', top: 0, zIndex: 1, minWidth: 100 }}>
                        Senior Partner
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, backgroundColor: 'primary.light', position: 'sticky', top: 0, zIndex: 1, minWidth: 80, textAlign: 'center' }}>
                        Members
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, backgroundColor: 'primary.light', position: 'sticky', top: 0, zIndex: 1, minWidth: 120 }}>
                        Senior Partner
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, backgroundColor: 'primary.light', position: 'sticky', top: 0, zIndex: 1, minWidth: 120 }}>
                        Partnership
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, backgroundColor: 'primary.light', position: 'sticky', top: 0, zIndex: 1, minWidth: 100 }}>
                        Invite Date
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {mlmuserLoading ? (
                      <TableRow>
                        <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                          <CircularProgress size={24} />
                        </TableCell>
                      </TableRow>
                    ) : filteredMlmusers.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                          No users found.
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredMlmusers.map((user, index) => (
                        <TableRow key={user.id || index} hover>
                          <TableCell sx={{ verticalAlign: 'top' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              {user.loginstatus === 1 ? (
                                <CheckCircleIcon sx={{ color: 'success.main', fontSize: 16 }} />
                              ) : (
                                <CheckCircleIcon sx={{ color: 'grey.400', fontSize: 16 }} />
                              )}
                              <Box>
                                <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.9rem' }}>
                                  {user.title || user.firstname || 'N/A'}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  ID: {user.id}
                                </Typography>
                              </Box>
                            </Box>
                          </TableCell>

                          <TableCell sx={{ verticalAlign: 'top' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <PhoneIcon sx={{ fontSize: '14px', color: 'text.secondary' }} />
                              <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                                {user.phone || user.mobilenumber || 'N/A'}
                              </Typography>
                            </Box>
                          </TableCell>

                          <TableCell sx={{ textAlign: 'center', verticalAlign: 'top' }}>
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: parseInt(user.membercount, 10) > 0 ? 'bold' : 'normal',
                                color: parseInt(user.membercount, 10) > 0 ? 'primary.main' : 'text.primary',
                                fontSize: '1rem',
                              }}
                            >
                              {user.membercount || '0'}
                            </Typography>
                          </TableCell>

                          <TableCell sx={{ verticalAlign: 'top' }}>
                            <Chip
                              icon={<PersonIcon />}
                              label={getSeniorPartnerId(user.seniorpartnerid)}
                              color={user.mlmusertypename === 'PARTNER' ? 'primary' : 'default'}
                              size="small"
                              sx={{ fontWeight: 600 }}
                            />
                          </TableCell>

                          <TableCell sx={{ verticalAlign: 'top' }}>
                            <Chip
                              label={user.partnershipstatus || 'PENDING'}
                              color={user.partnershipstatus === 'CONFIRMED' ? 'success' : 'warning'}
                              size="small"
                              sx={{ fontWeight: 600 }}
                            />
                          </TableCell>

                          <TableCell sx={{ verticalAlign: 'top' }}>
                            <Typography variant="body2" sx={{ fontSize: '0.85rem', color: 'text.primary' }}>
                              {formatDate(user.creationTimestamp)}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
}
